import { io, Socket } from 'socket.io-client';

/**
 * Kleiner Socket.io-Helper für die Turniersoftware.
 * Kanäle: `tm:<operationId>` (Dashboard/Zuschauer) und `board:<boardId>` (Scorer-Gerät).
 *
 * Verwendung:
 *   const unsubscribe = subscribeLive(`tm:${opId}`, {
 *     bracketUpdated: () => reload(),
 *     visitEntered: (data) => patch(data),
 *   });
 *   // Cleanup (useEffect-Return):
 *   return unsubscribe;
 */

const SOCKET_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

export type LiveEventHandler = (data: any) => void;
export type LiveEventHandlers = Record<string, LiveEventHandler>;

let socket: Socket | null = null;
let refCount = 0;

function acquireSocket(): Socket {
  if (!socket) {
    socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
    });
  }
  refCount += 1;
  return socket;
}

function releaseSocket(): void {
  refCount -= 1;
  if (refCount <= 0) {
    refCount = 0;
    if (socket) {
      socket.disconnect();
      socket = null;
    }
  }
}

/**
 * Abonniert einen Kanal und registriert Event-Callbacks.
 * Bei Reconnects wird der Kanal automatisch erneut abonniert.
 * Rückgabe: Cleanup-Funktion (Events abmelden, Kanal verlassen, Socket ggf. schließen).
 */
export function subscribeLive(channel: string, handlers: LiveEventHandlers): () => void {
  const s = acquireSocket();

  const entries = Object.entries(handlers);
  entries.forEach(([event, handler]) => s.on(event, handler));

  const doSubscribe = () => s.emit('subscribe', channel);
  if (s.connected) doSubscribe();
  s.on('connect', doSubscribe);

  let cleanedUp = false;
  return () => {
    if (cleanedUp) return;
    cleanedUp = true;
    s.off('connect', doSubscribe);
    entries.forEach(([event, handler]) => s.off(event, handler));
    if (s.connected) s.emit('unsubscribe', channel);
    releaseSocket();
  };
}

// ---------- Online Darts (Phase 2 Web, 2026-10-12): Partie-Raum `online:<matchId>` ----------

export interface OnlineJoinAck { ok: boolean; matchId?: string; role?: 'player' | 'camera' | 'spectator'; playerIdx?: number; error?: string }
export interface OnlineRoomHandle {
  /** Signal an einen Slot ({role, playerIdx}) oder an alle (null) – der Server leitet nur weiter */
  signal: (to: { role: 'player' | 'camera' | 'spectator'; playerIdx: number } | null, data: unknown) => void;
  leave: () => void;
}

/**
 * Tritt dem Partie-Raum mit dem Raum-Token bei (Spieler: roomToken aus der eigenen Partie-Sicht,
 * Kamera-Gerät: Token aus /api/online/camera/join). Nach jedem Reconnect wird erneut beigetreten
 * (`onJoined` feuert dann wieder, damit die Seite „hello“ an die anderen schickt).
 */
export function joinOnlineRoom(
  matchId: string,
  token: string,
  handlers: LiveEventHandlers,
  onJoined: (ack: OnlineJoinAck) => void,
): OnlineRoomHandle {
  const s = acquireSocket();
  const entries = Object.entries(handlers);
  entries.forEach(([event, handler]) => s.on(event, handler));

  const doJoin = () => s.emit('online:join', { matchId, token }, (ack: OnlineJoinAck) => onJoined(ack ?? { ok: false, error: 'Keine Antwort' }));
  if (s.connected) doJoin();
  s.on('connect', doJoin);

  let left = false;
  return {
    signal: (to, data) => { if (!left && s.connected) s.emit('online:signal', { to, data }); },
    leave: () => {
      if (left) return;
      left = true;
      s.off('connect', doJoin);
      entries.forEach(([event, handler]) => s.off(event, handler));
      if (s.connected) s.emit('online:leave');
      releaseSocket();
    },
  };
}

const liveSocket = { subscribeLive, joinOnlineRoom };
export default liveSocket;
